import React from "react";
import { useNavigate } from "react-router-dom";
import GameCard from "./GameCard";
import GameDetailsDialog from "./GameDetailsDialog";
import { useGameCheckout, useGames, useMe } from "../api/api_hooks";
import { paymentsEnabled } from "../featureFlags";
import { useRemoteControl, useRemotePage } from "../remote/RemoteControlContext";
import { useRemoteSelectionScroll } from "../remote/useRemoteSelectionScroll";

const getPaginationItems = (currentPage, totalPages) => {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const pages = new Set([1, totalPages, currentPage, currentPage - 1, currentPage + 1]);

  if (currentPage <= 3) {
    pages.add(2);
    pages.add(3);
    pages.add(4);
  }

  if (currentPage >= totalPages - 2) {
    pages.add(totalPages - 3);
    pages.add(totalPages - 2);
    pages.add(totalPages - 1);
  }

  const visiblePages = [...pages]
    .filter((pageNumber) => pageNumber >= 1 && pageNumber <= totalPages)
    .sort((a, b) => a - b);

  return visiblePages.flatMap((pageNumber, index) => {
    const previousPage = visiblePages[index - 1];
    if (index > 0 && pageNumber - previousPage > 1) {
      return [`ellipsis-${previousPage}-${pageNumber}`, pageNumber];
    }
    return [pageNumber];
  });
};

const PLAYTIME_RANGE = { min: 1, max: 120, step: 1 };
const PLAYER_RANGE = { min: 1, max: 20, step: 1 };

function InterfaceIcon({ name, className = "h-5 w-5" }) {
  const paths = {
    default: <><path d="M4 7h16M4 12h10M4 17h7" /><path d="m17 15 3 3-3 3" /></>,
    priceUp: <><path d="M12 3v18m4-14.5c0-1.38-1.79-2.5-4-2.5S8 5.12 8 6.5 9.79 9 12 9s4 1.12 4 2.5S14.21 14 12 14s-4 1.12-4 2.5S9.79 19 12 19s4-1.12 4-2.5" /><path d="m19 8 2-2 2 2" transform="translate(-2 -2)" /></>,
    priceDown: <><path d="M12 3v18m4-14.5c0-1.38-1.79-2.5-4-2.5S8 5.12 8 6.5 9.79 9 12 9s4 1.12 4 2.5S14.21 14 12 14s-4 1.12-4 2.5S9.79 19 12 19s4-1.12 4-2.5" /><path d="m17 18 2 2 2-2" /></>,
    clockUp: <><circle cx="11" cy="12" r="8" /><path d="M11 8v4l2.5 1.5M20 8V3m0 0-2 2m2-2 2 2" /></>,
    clockDown: <><circle cx="11" cy="12" r="8" /><path d="M11 8v4l2.5 1.5M20 16v5m0 0-2-2m2 2 2-2" /></>,
    star: <path d="m12 3 2.7 5.47 6.03.88-4.36 4.25 1.03 6-5.4-2.84-5.4 2.84 1.03-6-4.36-4.25 6.03-.88L12 3Z" />,
    reviews: <><path d="M5 4h14a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H9l-5 4v-4H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Z" /><path d="M7 8h10M7 12h7" /></>,
    motion: <><circle cx="12" cy="4.5" r="2" /><path d="m9.5 9 2.5 2 2.5-2m-5 0-3 4m8-4 3 4M12 11v4m0 0-3 5m3-5 3 5" /></>,
    playable: <><path d="M8.25 7h7.5a5 5 0 0 1 4.83 3.7l.9 3.35a2.7 2.7 0 0 1-4.45 2.69l-1.65-1.5H8.62l-1.65 1.5a2.7 2.7 0 0 1-4.45-2.69l.9-3.35A5 5 0 0 1 8.25 7Z" /><path d="M7.5 10.5v3m-1.5-1.5h3m7.5-.5h.01" /></>,
    time: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>,
    players: <><circle cx="9" cy="8" r="3" /><path d="M3.5 20a5.5 5.5 0 0 1 11 0m1-14.5a3 3 0 0 1 0 5.5m1.5 3a5.5 5.5 0 0 1 3.5 5" /></>,
    genre: <><path d="M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4z" /><circle cx="17" cy="17" r="3" /></>,
    tag: <><path d="M20 13 13 20l-9-9V4h7l9 9Z" /><circle cx="8" cy="8" r="1" /></>,
    competition: <><path d="M8 4h8v4a4 4 0 0 1-8 0V4Z" /><path d="M8 6H4v1a4 4 0 0 0 4 4m8-5h4v1a4 4 0 0 1-4 4M12 12v5m-4 3h8" /></>,
    difficulty: <><path d="M4 18a8 8 0 1 1 16 0" /><path d="m12 14 4-4M7 18h10" /></>,
    check: <path d="m5 12 4 4L19 6" />,
  };

  return <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{paths[name]}</svg>;
}

const initialFilters = {
  isMotionGame: false,
  playable: false,
  playtimeMin: PLAYTIME_RANGE.min,
  playtimeMax: PLAYTIME_RANGE.max,
  genres: "",
  tags: "",
  playersMin: PLAYER_RANGE.min,
  playersMax: PLAYER_RANGE.max,
  competitionType: "",
  difficulty: "",
};

const normalizeListFilter = (value) =>
  value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean)
    .join(",");

const sortOptions = [
  { value: "", label: "Featured", description: "Our default game order", icon: "default" },
  { value: "price__amount", label: "Price: Low to high", description: "Lowest price first", icon: "priceUp" },
  { value: "-price__amount", label: "Price: High to low", description: "Highest price first", icon: "priceDown" },
  { value: "playtime_estimate", label: "Quickest games", description: "Shortest playtime first", icon: "clockUp" },
  { value: "-playtime_estimate", label: "Longest games", description: "Longest playtime first", icon: "clockDown" },
  { value: "-avg_rating", label: "Top rated", description: "Highest player rating", icon: "star" },
  { value: "-review_count", label: "Most reviewed", description: "Most community reviews", icon: "reviews" },
];

function RangeFilter({
  label,
  icon,
  min,
  max,
  step,
  valueMin,
  valueMax,
  unit = "",
  onChange,
}) {
  const lowerValue = Number(valueMin);
  const upperValue = Number(valueMax);
  const lowerPercent = ((lowerValue - min) / (max - min)) * 100;
  const upperPercent = ((upperValue - min) / (max - min)) * 100;

  const handleLowerChange = (event) => {
    const nextValue = Math.min(Number(event.target.value), upperValue);
    onChange(nextValue, upperValue);
  };

  const handleUpperChange = (event) => {
    const nextValue = Math.max(Number(event.target.value), lowerValue);
    onChange(lowerValue, nextValue);
  };

  return (
    <div className="rounded-lg border border-white/10 bg-slate-950/30 px-4 py-3">
      <div className="flex items-center justify-between gap-3 text-xs font-semibold uppercase tracking-wide text-indigo-100/65">
        <span className="flex items-center gap-2"><InterfaceIcon name={icon} className="h-4 w-4 text-pink-300" />{label}</span>
        <span className="normal-case tracking-normal text-indigo-100">
          {lowerValue}{unit} - {upperValue}{unit}
        </span>
      </div>
      <div className="dual-range mt-3">
        <div className="absolute left-0 right-0 top-1/2 h-2 -translate-y-1/2 rounded-full bg-slate-900/90" />
        <div
          className="absolute top-1/2 h-2 -translate-y-1/2 rounded-full bg-pink-500"
          style={{
            left: `${lowerPercent}%`,
            right: `${100 - upperPercent}%`,
          }}
        />
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={lowerValue}
          onChange={handleLowerChange}
          aria-label={`${label} minimum`}
        />
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={upperValue}
          onChange={handleUpperChange}
          aria-label={`${label} maximum`}
        />
      </div>
    </div>
  );
}

export default function GameCards({ remoteHero }) {
  const { remoteActive } = useRemoteControl();
  const [page, setPage] = React.useState(1);
  const [searchTerm, setSearchTerm] = React.useState("");
  const [filters, setFilters] = React.useState(initialFilters);
  const [filtersOpen, setFiltersOpen] = React.useState(false);
  const [sortOpen, setSortOpen] = React.useState(false);
  const [sortValue, setSortValue] = React.useState("");
  const filterButtonRef = React.useRef(null);
  const sortButtonRef = React.useRef(null);
  const filterPanelRef = React.useRef(null);
  const sortPanelRef = React.useRef(null);
  const pageSize = 9;
  const [selectedGame, setSelectedGame] = React.useState(null);
  const [detailsRemoteState, setDetailsRemoteState] = React.useState(null);
  const detailsRemoteActionsRef = React.useRef({});
  const [focusedGameIndex, setFocusedGameIndex] = React.useState(0);
  const navigate = useNavigate();
  const { data: me } = useMe();
  const gameCheckout = useGameCheckout();
  const searchQuery = searchTerm.trim();
  const normalizedGenres = normalizeListFilter(filters.genres);
  const normalizedTags = normalizeListFilter(filters.tags);

  const gameParams = React.useMemo(
    () => ({
      page,
      page_size: pageSize,
      search: searchQuery,
      ...(filters.isMotionGame ? { is_motion_game: true } : {}),
      ...(filters.playable ? { playable: true } : {}),
      ...(filters.playtimeMin > PLAYTIME_RANGE.min ? { playtime_min: filters.playtimeMin } : {}),
      ...(filters.playtimeMax < PLAYTIME_RANGE.max ? { playtime_max: filters.playtimeMax } : {}),
      ...(normalizedGenres ? { genres: normalizedGenres } : {}),
      ...(normalizedTags ? { tags: normalizedTags } : {}),
      ...(filters.playersMin > PLAYER_RANGE.min ? { players_min: filters.playersMin } : {}),
      ...(filters.playersMax < PLAYER_RANGE.max ? { players_max: filters.playersMax } : {}),
      ...(filters.competitionType ? { competition_type: filters.competitionType } : {}),
      ...(filters.difficulty ? { difficulty: filters.difficulty } : {}),
      ...(sortValue ? { ordering: sortValue } : {}),
    }),
    [
      page,
      searchQuery,
      filters.isMotionGame,
      filters.playable,
      filters.playtimeMin,
      filters.playtimeMax,
      filters.playersMin,
      filters.playersMax,
      filters.competitionType,
      filters.difficulty,
      normalizedGenres,
      normalizedTags,
      sortValue,
    ]
  );

  const { data, isPending, isError, error } = useGames(gameParams);
  const visibleGames = data?.results || [];
  const focusedGame = visibleGames[focusedGameIndex] || null;
  const gameGridRef = useRemoteSelectionScroll(remoteActive, focusedGame?.id ?? focusedGame?.title ?? null);

  const hasTotalCount = typeof data?.count === "number";
  const totalGames = hasTotalCount ? data.count : 0;
  const totalPages = Math.max(1, Math.ceil(totalGames / pageSize));
  const paginationItems = getPaginationItems(page, totalPages);

  React.useEffect(() => {
    if (hasTotalCount && page > totalPages) {
      setPage(totalPages);
    }
  }, [hasTotalCount, page, totalPages]);

  React.useEffect(() => {
    setFocusedGameIndex((current) => Math.max(0, Math.min(current, visibleGames.length - 1)));
  }, [visibleGames.length]);

  React.useEffect(() => {
    if (!filtersOpen && !sortOpen) return undefined;

    const handlePointerDown = (event) => {
      if (
        filtersOpen &&
        !filterPanelRef.current?.contains(event.target) &&
        !filterButtonRef.current?.contains(event.target)
      ) {
        setFiltersOpen(false);
      }
      if (
        sortOpen &&
        !sortPanelRef.current?.contains(event.target) &&
        !sortButtonRef.current?.contains(event.target)
      ) {
        setSortOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setFiltersOpen(false);
        setSortOpen(false);
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [filtersOpen, sortOpen]);

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(1);
  };

  const handleFilterChange = (name, value) => {
    setFilters((currentFilters) => ({
      ...currentFilters,
      [name]: value,
    }));
    setPage(1);
  };

  const handleRangeFilterChange = (minName, maxName, minValue, maxValue) => {
    setFilters((currentFilters) => ({
      ...currentFilters,
      [minName]: minValue,
      [maxName]: maxValue,
    }));
    setPage(1);
  };

  const clearFilters = () => {
    setFilters(initialFilters);
    setPage(1);
  };

  const handleSortChange = (value) => {
    setSortValue(value);
    setSortOpen(false);
    setPage(1);
  };

  const clearSorting = () => {
    setSortValue("");
    setPage(1);
  };

  const activeFilterCount = [
    filters.isMotionGame,
    filters.playable,
    filters.playtimeMin > PLAYTIME_RANGE.min,
    filters.playtimeMax < PLAYTIME_RANGE.max,
    normalizedGenres,
    normalizedTags,
    filters.playersMin > PLAYER_RANGE.min,
    filters.playersMax < PLAYER_RANGE.max,
    filters.competitionType,
    filters.difficulty,
  ].filter(Boolean).length;
  const selectedSortLabel =
    sortOptions.find((option) => option.value === sortValue)?.label ?? "Default";

  const closeDialog = () => setSelectedGame(null);

  const toGamePath = (title) => {
    if (!title) return null;
    const slug = title
      .toLowerCase()
      .replace(/\s+/g, "_")
      .replace(/[^a-z0-9_-]/g, "");
    return slug || null;
  };

  const handlePlay = (game) => {
    if (!game) return;
    const slug = toGamePath(game.title);
    if (slug) {
      navigate(`/play/${slug}`);
    }
    closeDialog();
  };

  const handleBuy = (game) => {
    if (!game?.id) return;
    if (!paymentsEnabled) {
      navigate("/subscriptions");
      closeDialog();
      return;
    }
    if (!me) {
      navigate("/login");
      return;
    }

    const origin = window.location.origin;
    gameCheckout.mutate(
      {
        gameId: game.id,
        successUrl: `${origin}/?checkout=game-success`,
        cancelUrl: `${origin}/?checkout=game-cancelled`,
      },
      {
        onSuccess: (data) => {
          if (data?.url) window.location.assign(data.url);
        },
      }
    );
  };

  const handleSubscribe = () => {
    navigate("/subscriptions");
  };

  const handleDetailsRemoteState = React.useCallback((nextState, actions) => {
    detailsRemoteActionsRef.current = actions || {};
    setDetailsRemoteState((current) => JSON.stringify(current) === JSON.stringify(nextState) ? current : nextState);
  }, []);

  const moveFocus = (direction) => {
    if (!visibleGames.length) return;
    setFocusedGameIndex((current) => (current + direction + visibleGames.length) % visibleGames.length);
  };

  const setRemoteRange = (name, rawValue) => {
    const value = Number(rawValue);
    setFilters((current) => {
      if (name === "playtimeMin") return { ...current, playtimeMin: Math.min(value, current.playtimeMax) };
      if (name === "playtimeMax") return { ...current, playtimeMax: Math.max(value, current.playtimeMin) };
      if (name === "playersMin") return { ...current, playersMin: Math.min(value, current.playersMax) };
      return { ...current, playersMax: Math.max(value, current.playersMin) };
    });
    setPage(1);
  };

  useRemotePage({
    id: "games",
    title: "Games",
    subtitle: selectedGame?.title || focusedGame?.title || remoteHero?.title || "Browse the game library",
    state: {
      page,
      totalPages,
      totalGames,
      focusedGame: focusedGame?.title || "",
      selectedGame: selectedGame?.title || "",
      searchTerm,
      filters,
      sortValue,
      activeSlide: remoteHero?.activeSlide,
    },
    sections: [
      ...(remoteHero ? [{ title: "Featured", controls: [
        { id: "home.previousSlide", type: "button", label: "Previous slide" },
        { id: "home.nextSlide", type: "button", label: "Next slide" },
      ] }] : []),
      { title: `Games (${totalGames})`, controls: [
        { id: "games.previous", type: "button", label: "Previous game", disabled: !visibleGames.length },
        { id: "games.next", type: "button", label: "Next game", disabled: !visibleGames.length },
        { id: "games.select", type: "button", label: selectedGame ? "Close details" : "Open details", disabled: !focusedGame },
        { id: "games.play", type: "button", label: "Play", disabled: !(selectedGame || focusedGame)?.user_has_access },
        { id: "games.previousPage", type: "button", label: "Previous page", disabled: page <= 1 },
        { id: "games.nextPage", type: "button", label: "Next page", disabled: page >= totalPages },
      ] },
      ...(selectedGame ? [{ title: "Game details", controls: [
        { id: "details.previousMedia", type: "button", label: "Previous media", disabled: (detailsRemoteState?.mediaCount || 0) < 2 },
        { id: "details.nextMedia", type: "button", label: "Next media", disabled: (detailsRemoteState?.mediaCount || 0) < 2 },
        { id: "details.close", type: "button", label: "Close details" },
        { id: "details.play", type: "button", label: "Play game", disabled: !selectedGame.user_has_access },
        { id: "details.plans", type: "button", label: "View plans", disabled: Boolean(selectedGame.user_has_access) },
      ] }] : []),
      { title: "Search and sort", controls: [
        { id: "games.search", type: "text", label: "Search games", value: searchTerm, maxLength: 100, placeholder: "Game title" },
        { id: "games.sort", type: "select", label: "Sort", value: sortValue, options: sortOptions.map(({ value, label }) => ({ value, label })) },
      ] },
      { title: "Filters", controls: [
        { id: "games.motion", type: "toggle", label: "Motion games", value: filters.isMotionGame },
        { id: "games.playable", type: "toggle", label: "Playable for me", value: filters.playable },
        { id: "games.competition", type: "select", label: "Competition", value: filters.competitionType, options: ["Competitive", "Cooperative", "Mixed"].map((value) => ({ value, label: value })) },
        { id: "games.difficulty", type: "select", label: "Difficulty", value: filters.difficulty, options: ["Easy", "Medium", "Hard", "Expert"].map((value) => ({ value, label: value })) },
        { id: "games.playtimeMin", type: "range", label: "Minimum playtime", value: filters.playtimeMin, min: PLAYTIME_RANGE.min, max: PLAYTIME_RANGE.max, unit: " min" },
        { id: "games.playtimeMax", type: "range", label: "Maximum playtime", value: filters.playtimeMax, min: PLAYTIME_RANGE.min, max: PLAYTIME_RANGE.max, unit: " min" },
        { id: "games.playersMin", type: "range", label: "Minimum players", value: filters.playersMin, min: PLAYER_RANGE.min, max: PLAYER_RANGE.max },
        { id: "games.playersMax", type: "range", label: "Maximum players", value: filters.playersMax, min: PLAYER_RANGE.min, max: PLAYER_RANGE.max },
        { id: "games.genres", type: "text", label: "Genres", value: filters.genres, maxLength: 100, placeholder: "Action, Puzzle" },
        { id: "games.tags", type: "text", label: "Tags", value: filters.tags, maxLength: 100, placeholder: "Family, Party" },
        { id: "games.clearFilters", type: "button", label: "Clear filters", disabled: !activeFilterCount },
      ] },
    ],
    actions: {
      "home.previousSlide": () => remoteHero?.previous(),
      "home.nextSlide": () => remoteHero?.next(),
      "games.previous": () => moveFocus(-1),
      "games.next": () => moveFocus(1),
      "games.select": () => setSelectedGame((current) => current ? null : focusedGame),
      "games.play": () => handlePlay(selectedGame || focusedGame),
      "games.previousPage": () => setPage((current) => Math.max(1, current - 1)),
      "games.nextPage": () => setPage((current) => Math.min(totalPages, current + 1)),
      "games.search": ({ value }) => { setSearchTerm(String(value || "").slice(0, 100)); setPage(1); },
      "games.sort": ({ value }) => handleSortChange(String(value || "")),
      "games.motion": ({ value }) => handleFilterChange("isMotionGame", Boolean(value)),
      "games.playable": ({ value }) => handleFilterChange("playable", Boolean(value)),
      "games.competition": ({ value }) => handleFilterChange("competitionType", String(value || "")),
      "games.difficulty": ({ value }) => handleFilterChange("difficulty", String(value || "")),
      "games.playtimeMin": ({ value }) => setRemoteRange("playtimeMin", value),
      "games.playtimeMax": ({ value }) => setRemoteRange("playtimeMax", value),
      "games.playersMin": ({ value }) => setRemoteRange("playersMin", value),
      "games.playersMax": ({ value }) => setRemoteRange("playersMax", value),
      "games.genres": ({ value }) => handleFilterChange("genres", String(value || "").slice(0, 100)),
      "games.tags": ({ value }) => handleFilterChange("tags", String(value || "").slice(0, 100)),
      "games.clearFilters": clearFilters,
      "details.previousMedia": () => detailsRemoteActionsRef.current.previous?.(),
      "details.nextMedia": () => detailsRemoteActionsRef.current.next?.(),
      "details.close": closeDialog,
      "details.play": () => handlePlay(selectedGame),
      "details.plans": handleSubscribe,
    },
  });

  return (
    <section className="mt-12">
      <div className="relative z-30 mb-8 w-full">
        <label htmlFor="game-search" className="sr-only">
          Search games
        </label>
        <div className="mx-auto flex max-w-2xl items-center gap-3">
          <div className="relative min-w-0 flex-1">
            <span className="pointer-events-none absolute inset-y-0 left-4 flex items-center text-indigo-100/55">
              <svg
                className="h-5 w-5"
                viewBox="0 0 20 20"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                aria-hidden="true"
              >
                <circle cx="9" cy="9" r="5.5" />
                <path d="M13.2 13.2 17 17" strokeLinecap="round" />
              </svg>
            </span>
            <input
              id="game-search"
              type="search"
              value={searchTerm}
              onChange={handleSearchChange}
              placeholder="Search games"
              className="h-14 w-full rounded-full border border-white/10 bg-indigo-950/60 px-12 text-base font-medium text-white shadow-xl outline-none backdrop-blur placeholder:text-indigo-100/45 focus:border-pink-300/70 focus:ring-2 focus:ring-pink-300/30"
            />
          </div>
          <div ref={filterButtonRef} className="relative flex-none">
            <button
              type="button"
              onClick={() => {
                setSortOpen(false);
                setFiltersOpen((open) => !open);
              }}
              aria-label={filtersOpen ? "Close filters" : "Open filters"}
              aria-expanded={filtersOpen}
              className={`grid h-14 w-14 place-items-center rounded-full border text-indigo-100 shadow-xl transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pink-300 ${
                filtersOpen || activeFilterCount
                  ? "border-pink-300/60 bg-pink-500/80 text-white"
                  : "border-white/10 bg-indigo-950/60 hover:bg-purple-500/80"
              }`}
            >
              <svg
                className="h-5 w-5"
                viewBox="0 0 20 20"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                aria-hidden="true"
              >
                <path d="M3 5h14M6 10h8M8 15h4" strokeLinecap="round" />
              </svg>
            </button>
            {activeFilterCount > 0 ? (
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  clearFilters();
                }}
                aria-label="Clear all filters"
                title="Clear all filters"
                className="absolute -right-1 -top-1 grid h-5 w-5 place-items-center rounded-full bg-white text-sm font-black leading-none text-pink-600 shadow-md transition hover:scale-110 hover:bg-pink-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pink-300"
              >
                <span aria-hidden="true">×</span>
              </button>
            ) : null}
          </div>
          <div ref={sortButtonRef} className="relative flex-none">
            <button
              type="button"
              onClick={() => {
                setFiltersOpen(false);
                setSortOpen((open) => !open);
              }}
              aria-label={sortOpen ? "Close sorting" : "Open sorting"}
              aria-expanded={sortOpen}
              className={`grid h-14 w-14 place-items-center rounded-full border text-indigo-100 shadow-xl transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pink-300 ${
                sortOpen || sortValue
                  ? "border-pink-300/60 bg-pink-500/80 text-white"
                  : "border-white/10 bg-indigo-950/60 hover:bg-purple-500/80"
              }`}
            >
              <svg
                className="h-5 w-5"
                viewBox="0 0 20 20"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                aria-hidden="true"
              >
                <path d="M5 4v12M5 16l-2-2M5 16l2-2M15 4v12M15 4l-2 2M15 4l2 2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            {sortValue ? (
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  clearSorting();
                }}
                aria-label="Clear sorting"
                title="Clear sorting"
                className="absolute -right-1 -top-1 grid h-5 w-5 place-items-center rounded-full bg-white text-sm font-black leading-none text-pink-600 shadow-md transition hover:scale-110 hover:bg-pink-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pink-300"
              >
                <span aria-hidden="true">×</span>
              </button>
            ) : null}
          </div>
        </div>

        {filtersOpen ? (
          <div ref={filterPanelRef} className="absolute right-0 top-[calc(100%+0.75rem)] z-40 max-h-[min(70vh,720px)] w-full overflow-y-auto rounded-3xl border border-white/10 bg-slate-950/95 p-4 text-left shadow-2xl shadow-black/50 ring-1 ring-white/5 backdrop-blur-2xl sm:right-1/2 sm:w-[44rem] sm:translate-x-1/2 sm:p-5">
            <div className="mb-4 flex items-center justify-between gap-4 border-b border-white/10 pb-4">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-pink-500/15 text-pink-300 ring-1 ring-pink-300/20">
                  <svg className="h-5 w-5" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><path d="M3 5h14M6 10h8M8 15h4" strokeLinecap="round" /></svg>
                </span>
                <div>
                  <h3 className="text-base font-extrabold text-white">Refine your games</h3>
                  <p className="text-xs text-indigo-100/50">Results update as you choose</p>
                </div>
              </div>
              {activeFilterCount > 0 && <span className="rounded-full bg-pink-400/10 px-3 py-1 text-xs font-bold text-pink-200">{activeFilterCount} active</span>}
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <label className={`flex min-h-14 cursor-pointer items-center gap-3 rounded-2xl border px-4 text-sm font-semibold transition ${filters.isMotionGame ? "border-pink-300/40 bg-pink-500/10 text-white" : "border-white/10 bg-white/[0.035] text-indigo-100 hover:bg-white/[0.06]"}`}>
                <input
                  type="checkbox"
                  checked={filters.isMotionGame}
                  onChange={(event) => handleFilterChange("isMotionGame", event.target.checked)}
                  className="sr-only"
                />
                <span className={`flex h-9 w-9 items-center justify-center rounded-xl ${filters.isMotionGame ? "bg-pink-500/20 text-pink-200" : "bg-indigo-400/10 text-indigo-200/60"}`}><InterfaceIcon name="motion" /></span>
                <span className="flex-1">Motion games<span className="block text-xs font-normal text-indigo-100/45">Play using body movement</span></span>
                {filters.isMotionGame && <InterfaceIcon name="check" className="h-4 w-4 text-pink-300" />}
              </label>

              <label className={`flex min-h-14 cursor-pointer items-center gap-3 rounded-2xl border px-4 text-sm font-semibold transition ${filters.playable ? "border-pink-300/40 bg-pink-500/10 text-white" : "border-white/10 bg-white/[0.035] text-indigo-100 hover:bg-white/[0.06]"}`}>
                <input
                  type="checkbox"
                  checked={filters.playable}
                  onChange={(event) => handleFilterChange("playable", event.target.checked)}
                  className="sr-only"
                />
                <span className={`flex h-9 w-9 items-center justify-center rounded-xl ${filters.playable ? "bg-pink-500/20 text-pink-200" : "bg-indigo-400/10 text-indigo-200/60"}`}><InterfaceIcon name="playable" /></span>
                <span className="flex-1">Playable for me<span className="block text-xs font-normal text-indigo-100/45">Games you can launch now</span></span>
                {filters.playable && <InterfaceIcon name="check" className="h-4 w-4 text-pink-300" />}
              </label>

              <fieldset className="rounded-2xl border border-white/10 bg-white/[0.035] p-3 sm:col-span-2">
                <legend className="px-1 text-xs font-bold uppercase tracking-[0.16em] text-indigo-100/55">
                  <span className="flex items-center gap-2"><InterfaceIcon name="competition" className="h-4 w-4 text-pink-300" />Competition type</span>
                </legend>
                <div className="mt-1 grid grid-cols-3 gap-2">
                  {[
                    { value: "Competitive", label: "Competitive", hint: "Face off" },
                    { value: "Cooperative", label: "Co-op", hint: "Team up" },
                    { value: "Mixed", label: "Mixed", hint: "Both styles" },
                  ].map((option) => (
                    <button key={option.value} type="button" onClick={() => handleFilterChange("competitionType", filters.competitionType === option.value ? "" : option.value)} aria-pressed={filters.competitionType === option.value} className={`rounded-xl border px-2 py-2.5 text-center transition ${filters.competitionType === option.value ? "border-pink-300/50 bg-gradient-to-br from-violet-500/25 to-pink-500/20 text-white shadow-lg" : "border-white/5 bg-slate-950/35 text-indigo-100/65 hover:border-white/15 hover:bg-white/[0.05]"}`}>
                      <span className="block text-xs font-bold sm:text-sm">{option.label}</span>
                      <span className="mt-0.5 block text-[10px] text-indigo-100/45">{option.hint}</span>
                    </button>
                  ))}
                </div>
              </fieldset>

              <fieldset className="rounded-2xl border border-white/10 bg-white/[0.035] p-3 sm:col-span-2">
                <legend className="px-1 text-xs font-bold uppercase tracking-[0.16em] text-indigo-100/55">
                  <span className="flex items-center gap-2"><InterfaceIcon name="difficulty" className="h-4 w-4 text-pink-300" />Difficulty</span>
                </legend>
                <div className="mt-1 grid grid-cols-2 gap-2 sm:grid-cols-4">
                  {["Easy", "Medium", "Hard", "Expert"].map((level, index) => (
                    <button key={level} type="button" onClick={() => handleFilterChange("difficulty", filters.difficulty === level ? "" : level)} aria-pressed={filters.difficulty === level} className={`flex items-center justify-center gap-2 rounded-xl border px-3 py-2.5 text-xs font-bold transition sm:text-sm ${filters.difficulty === level ? "border-pink-300/50 bg-pink-500/15 text-white" : "border-white/5 bg-slate-950/35 text-indigo-100/65 hover:border-white/15 hover:bg-white/[0.05]"}`}>
                      <span className={`h-2 w-2 rounded-full ${["bg-emerald-400", "bg-amber-300", "bg-orange-400", "bg-rose-400"][index]}`} />{level}
                    </button>
                  ))}
                </div>
              </fieldset>

              <div className="sm:col-span-2">
                <RangeFilter
                  label="Playtime"
                  icon="time"
                  min={PLAYTIME_RANGE.min}
                  max={PLAYTIME_RANGE.max}
                  step={PLAYTIME_RANGE.step}
                  valueMin={filters.playtimeMin}
                  valueMax={filters.playtimeMax}
                  unit=" min"
                  onChange={(minValue, maxValue) =>
                    handleRangeFilterChange("playtimeMin", "playtimeMax", minValue, maxValue)
                  }
                />
              </div>

              <div className="sm:col-span-2">
                <RangeFilter
                  label="Players"
                  icon="players"
                  min={PLAYER_RANGE.min}
                  max={PLAYER_RANGE.max}
                  step={PLAYER_RANGE.step}
                  valueMin={filters.playersMin}
                  valueMax={filters.playersMax}
                  onChange={(minValue, maxValue) =>
                    handleRangeFilterChange("playersMin", "playersMax", minValue, maxValue)
                  }
                />
              </div>

              <label className="flex flex-col gap-2 rounded-2xl border border-white/10 bg-white/[0.035] p-3 text-xs font-semibold uppercase tracking-wide text-indigo-100/65">
                <span className="flex items-center gap-2"><InterfaceIcon name="genre" className="h-4 w-4 text-pink-300" />Genres</span>
                <input
                  type="text"
                  value={filters.genres}
                  onChange={(event) => handleFilterChange("genres", event.target.value)}
                  className="h-11 rounded-xl border border-white/10 bg-slate-950/50 px-3 text-sm font-medium normal-case tracking-normal text-white outline-none placeholder:text-indigo-100/35 focus:border-pink-300/70 focus:ring-2 focus:ring-pink-300/30"
                  placeholder="Action, Puzzle"
                />
              </label>

              <label className="flex flex-col gap-2 rounded-2xl border border-white/10 bg-white/[0.035] p-3 text-xs font-semibold uppercase tracking-wide text-indigo-100/65">
                <span className="flex items-center gap-2"><InterfaceIcon name="tag" className="h-4 w-4 text-pink-300" />Tags</span>
                <input
                  type="text"
                  value={filters.tags}
                  onChange={(event) => handleFilterChange("tags", event.target.value)}
                  className="h-11 rounded-xl border border-white/10 bg-slate-950/50 px-3 text-sm font-medium normal-case tracking-normal text-white outline-none placeholder:text-indigo-100/35 focus:border-pink-300/70 focus:ring-2 focus:ring-pink-300/30"
                  placeholder="Family, Party"
                />
              </label>
            </div>

            {activeFilterCount > 0 ? (
              <div className="mt-4 flex justify-end border-t border-white/10 pt-4">
                <button
                  type="button"
                  onClick={clearFilters}
                  className="rounded-xl border border-white/10 bg-white/[0.05] px-4 py-2.5 text-sm font-semibold text-indigo-100 transition-colors hover:border-pink-300/30 hover:bg-pink-500/15 hover:text-white"
                >
                  Clear filters
                </button>
              </div>
            ) : null}
          </div>
        ) : null}

        {sortOpen ? (
          <div ref={sortPanelRef} className="absolute right-0 top-[calc(100%+0.75rem)] z-40 w-full rounded-3xl border border-white/10 bg-slate-950/95 p-3 text-left shadow-2xl shadow-black/50 ring-1 ring-white/5 backdrop-blur-2xl sm:right-1/2 sm:w-[24rem] sm:translate-x-1/2">
            <div className="mb-2 flex items-center gap-3 border-b border-white/10 px-2 pb-3 pt-1">
              <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-violet-500/15 text-violet-200 ring-1 ring-violet-300/20">
                <svg className="h-5 w-5" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><path d="M5 4v12M5 16l-2-2M5 16l2-2M15 4v12M15 4l-2 2M15 4l2 2" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </span>
              <div>
                <h3 className="text-sm font-extrabold text-white">Sort games</h3>
                <p className="text-xs text-indigo-100/45">Choose how results are ordered</p>
              </div>
            </div>
            <div className="grid gap-1">
              {sortOptions.map((option) => (
                <button
                  key={option.value || "default"}
                  type="button"
                  onClick={() => handleSortChange(option.value)}
                  className={`group flex min-h-[3.5rem] items-center gap-3 rounded-2xl border px-3 text-left transition-colors ${
                    option.value === sortValue
                      ? "border-pink-300/30 bg-pink-500/15 text-white"
                      : "border-transparent text-indigo-100 hover:border-white/5 hover:bg-white/[0.045]"
                  }`}
                  aria-pressed={option.value === sortValue}
                >
                  <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${option.value === sortValue ? "bg-pink-500/20 text-pink-200" : "bg-indigo-400/10 text-indigo-200/55 group-hover:text-indigo-100"}`}><InterfaceIcon name={option.icon} /></span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-bold">{option.label}</span>
                    <span className="block text-xs font-normal text-indigo-100/40">{option.description}</span>
                  </span>
                  {option.value === sortValue && <span className="flex h-6 w-6 items-center justify-center rounded-full bg-pink-400 text-slate-950"><InterfaceIcon name="check" className="h-3.5 w-3.5" /></span>}
                </button>
              ))}
            </div>
            <div className="mt-2 border-t border-white/10 px-3 pb-1 pt-3 text-xs text-indigo-100/45">
              Current order: <span className="font-semibold text-indigo-100/70">{selectedSortLabel}</span>
            </div>
          </div>
        ) : null}
      </div>
      <div ref={gameGridRef} className="grid grid-cols-1 gap-8 sm:grid-cols-2 xl:grid-cols-3">
        {isPending ? (
          <div className="col-span-full py-12 text-center text-indigo-100/70">Loading...</div>
        ) : isError ? (
          <div className="col-span-full py-12 text-center text-pink-100">
            Error: {error.message}
          </div>
        ) : data?.results?.length ? (
          data.results.map((game, index) => (
            <GameCard
              key={game.id ?? game.title}
              {...game}
              onSelect={() => setSelectedGame(game)}
              remoteFocused={remoteActive && index === focusedGameIndex}
            />
          ))
        ) : (
          <div className="col-span-full py-12 text-center text-indigo-100/70">
            No games found.
          </div>
        )}
      </div>
      {totalPages > 1 ? (
        <nav className="mt-12 flex justify-center" aria-label="Games pages">
          <div className="flex flex-wrap items-center justify-center gap-2">
            {paginationItems.map((item) =>
              typeof item === "number" ? (
                <button
                  key={item}
                  type="button"
                  className={`grid h-11 min-w-11 place-items-center rounded-full px-4 text-sm font-bold shadow-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pink-300 ${
                    item === page
                      ? "bg-pink-500 text-white shadow-pink-500/30"
                      : "border border-white/10 bg-indigo-950/60 text-indigo-100 hover:bg-purple-500/80 hover:text-white"
                  }`}
                  aria-current={item === page ? "page" : undefined}
                  aria-label={`Go to page ${item}`}
                  onClick={() => setPage(item)}
                >
                  {item}
                </button>
              ) : (
                <span
                  key={item}
                  className="grid h-11 min-w-8 place-items-center text-sm font-bold text-indigo-100/60"
                  aria-hidden="true"
                >
                  ...
                </span>
              )
            )}
          </div>
        </nav>
      ) : null}

      {paymentsEnabled && gameCheckout.isError ? (
        <div className="mt-6 rounded-2xl border border-pink-300/25 bg-pink-500/10 px-5 py-3 text-sm text-pink-100">
          {gameCheckout.error?.response?.data?.detail || "Could not start Stripe checkout."}
        </div>
      ) : null}

      <GameDetailsDialog
        game={selectedGame}
        onClose={closeDialog}
        onPlay={handlePlay}
        onBuy={handleBuy}
        onSubscribe={handleSubscribe}
        isBuying={gameCheckout.isPending}
        purchasesEnabled={paymentsEnabled}
        onRemoteStateChange={handleDetailsRemoteState}
      />
    </section>
  );
}
